<template>
  <div class="wrapper">
    <h2>
      API Keys
      <el-button type="text">
        Learn more about API authentication
        <i class="el-icon-right" />
      </el-button>
    </h2>

    <el-card id="account-tokens">
      <h3>
        <el-button type="primary" @click="showCreate = true">Create API Key</el-button>
        These keys will allow you to authenticate API requests
      </h3>
      <el-table v-loading="loading" :data="tokens">
        <el-table-column label="Name" prop="name" />

        <el-table-column label="Last Used" prop="used" sortable>
          <template slot-scope="scope">
            {{ scope.row.used ? toCalendar(scope.row.used) : 'Never' }}
          </template>
        </el-table-column>

        <el-table-column label="Created" prop="created" sortable>
          <template slot-scope="scope">
            {{ toCalendar(scope.row.created) }}
          </template>
        </el-table-column>

        <el-table-column align="right">
          <template slot-scope="scope">
            <el-button type="danger" size="mini" @click="revoke(scope.row)">Revoke</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog title="Create API key" :visible.sync="showCreate" width="500px">
        <el-form
          ref="token"
          :model="form"
          :rules="rules"
          size="small"
          label-position="left"
          label-width="150px"
        >
          <el-form-item label="Key name" prop="name">
            <el-input v-model="form.name" />
          </el-form-item>

          <el-form-item label="Project" prop="projectId">
            <el-select v-model="form.projectId" placeholder="Select a project">
              <el-option
                v-for="project in projects"
                :key="project.id"
                :label="project.name"
                :value="project.id"
              />
            </el-select>
          </el-form-item>

          <el-divider />
          <el-form-item class="title" label="Resource type">Permissions</el-form-item>

          <el-form-item v-for="resource in resources" :key="resource" :label="resource">
            <el-radio-group v-model="form.resources[resource]">
              <el-radio-button :label="0">None</el-radio-button>
              <el-radio-button :label="1">Read</el-radio-button>
              <el-radio-button :label="3">Write</el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-form>

        <div slot="footer">
          <el-button type="default" @click="showCreate = false">Cancel</el-button>
          <el-button type="primary" :loading="loading" @click="save">Create</el-button>
        </div>
      </el-dialog>
    </el-card>

    <el-dialog title="You've registered your API Key" :visible.sync="showKey" width="600px">
      <strong>Make sure you copy this down.<br>If you lose it you'll need to re-create the token.</strong>

      <p class="token">
        <Clipboard :value="key" />
      </p>

      <el-button type="primary" @click="reset">Got it</el-button>

      <el-alert
        type="warning"
        title="Runstack is not going to store this key on my behalf for security reasons."
        :closable="false"
        show-icon
      />
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Clipboard from '../components/Clipboard'

export default {
  components: {
    Clipboard
  },
  data () {
    return {
      showCreate: false,
      showKey: false,
      loading: true,
      key: '',
      tokens: [],
      resources: [
        'applications',
        'domains',
        'instances',
        'tasks'
      ],
      form: {
        name: null,
        projectId: null,
        resources: {}
      },
      rules: {
        name: [{ required: true, message: 'Please input a Key Name', trigger: 'blur' }],
        projectId: [{ required: true, message: 'Please choose a Project', trigger: 'change' }]
      }
    }
  },
  computed: {
    projects () {
      return this.user.roles.map((r) => r.project)
    },
    ...mapState(['user'])
  },
  created () {
    this.$store.dispatch('getTokens').then((tokens) => {
      this.tokens = tokens
      this.loading = false
    })
  },
  methods: {
    save () {
      this.$refs.token.validate((valid) => {
        if (valid) {
          this.loading = true
          this.$store.dispatch('createToken', this.form).then((res) => {
            this.loading = false
            this.showCreate = false
            this.showKey = true
            this.form = {
              name: null,
              projectId: null,
              resources: {}
            }

            this.key = res.key
            this.tokens.push(res.token)
          })
        }
      })
    },
    revoke (token) {
      this.$confirm('You will be unable to perform any more actions with this key. Continue?', 'Warning', {
        confirmButtonText: 'Revoke Key',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'el-button--danger',
        type: 'warning'
      }).then(() => this.$store.dispatch('revokeToken', token))
        .then(() => {
          this.tokens = this.tokens.filter((t) => t.id !== token.id)
        })
        .catch(() => {})
    },
    reset () {
      this.showKey = false
      this.key = null
    }
  },
  watch: {
    showCreate (value, old) {
      if (value && !old) {
        this.resources.forEach((resource) => this.$set(this.form.resources, resource, 0))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../theme";

  h3 {
    border-bottom: 1px solid $--border-color-base;
    margin-bottom: 24px;
    padding-bottom: 24px;
  }

  .token {
    background-color: $--background-color-base;
    padding: 10px;
  }
  .token + .el-button {
    width: 100%;
    margin-bottom: 20px;
  }
  .el-alert {
    word-break: break-word;
  }
</style>

<style lang="scss">
  @import "../theme";

  #account-tokens {
    h3 {
      .el-button {
        float: right;
        margin-top: -10px;
      }
    }
    .el-form-item__label {
      text-transform: capitalize;
    }
    .el-form-item__content {
      text-align: right;
    }
    .el-form-item.title {
      .el-form-item__label, .el-form-item__content {
        text-transform: uppercase;
        font-weight: 500;
        color: $--color-text-primary;
      }
    }
  }
</style>
